import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { ledgerMappingService } from 'api/services/ledgerMappingService';
import { bankAccountService } from 'api/services/bankAccountService';
import { propertyService } from 'api/services/propertyService';
import type { AccountingLedgerMap, FbLedger, MappingLookups, UpsertAccountingLedgerMapRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

interface LedgerMappingModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: AccountingLedgerMap | null;
  lookups?: MappingLookups | null;
  onSave: () => void;
  onClose: () => void;
}

interface LedgerMappingFormData {
  propertyId: string;
  sourceType: string;
  sourceKey: string;
  ledgerRef: string;
  notes: string;
}

const INITIAL_FORM_DATA: LedgerMappingFormData = {
  propertyId: '',
  sourceType: 'BANK_ACCOUNT',
  sourceKey: '',
  ledgerRef: '',
  notes: '',
};

/**
 * Add/Edit one resolver row: which Fairbooks ledger a bank account or an
 * accounting role posts to. The SOURCE of a mapping is fixed after creation
 * (backend rule) — remapping a different source is delete + create, so history
 * stays honest. Edit only changes the target ledger and notes.
 */
const LedgerMappingModal: React.FC<LedgerMappingModalProps> = ({
  isOpen, mode, record, lookups, onSave, onClose,
}) => {
  const [formData, setFormData] = useState<LedgerMappingFormData>(INITIAL_FORM_DATA);
  const initialSnapshotRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Ledger metadata for whatever the user picked, so create/update can send
  // ledgerCode/ledgerName snapshots alongside the id.
  const fbLedgerCacheRef = useRef<Record<string, FbLedger>>({});

  useEffect(() => {
    if (!isOpen) return;
    const baseData: LedgerMappingFormData = mode === 'edit' && record
      ? {
          propertyId: record.propertyId || '',
          sourceType: record.sourceType,
          sourceKey: record.sourceKey,
          ledgerRef: record.ledgerRef,
          notes: record.notes || '',
        }
      : { ...INITIAL_FORM_DATA };
    if (mode === 'edit' && record && record.ledgerCode) {
      fbLedgerCacheRef.current[record.ledgerRef] = {
        id: record.ledgerRef,
        ledgerCode: record.ledgerCode,
        ledgerName: record.ledgerName || '',
        normalBalance: '',
        ledgerNature: '',
      };
    }
    setFormData(baseData);
    initialSnapshotRef.current = JSON.stringify(baseData);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const loadBankAccountOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await bankAccountService.getAll({ searchTerm: input || undefined, pageSize: 100 });
    return res.data.map(b => ({ value: b.id, label: `${b.accountName} (${b.bankName})` }));
  }, []);

  const lastLedgerErrorRef = useRef('');
  const loadFbLedgerOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await ledgerMappingService.getFbLedgers({
        propertyId: formData.propertyId || undefined,
        search: input || undefined,
      });
      lastLedgerErrorRef.current = '';
      const ledgers = res.data ?? [];
      ledgers.forEach(l => { fbLedgerCacheRef.current[l.id] = l; });
      return ledgers.map(l => ({
        value: l.id,
        label: `${l.ledgerCode} - ${l.ledgerName}${l.groupName ? ` (${l.groupName})` : ''}`,
      }));
    } catch (err: any) {
      // Entity-scope setup errors (property not wired, multiple entities) come
      // back as 400s with an actionable message — show it once, not per keystroke.
      const msg = err?.response?.data?.message || 'Could not load Fairbooks ledgers.';
      if (lastLedgerErrorRef.current !== msg) {
        lastLedgerErrorRef.current = msg;
        notificationService.toast.error(msg, 6000);
      }
      return [];
    }
  }, [formData.propertyId]);

  const roleOptions: SelectOption[] = useMemo(
    () => (lookups?.accountingRoles ?? ['GUEST_ADVANCE']).map(r => ({ value: r, label: r })),
    [lookups]
  );

  const sourceTypeOptions: SelectOption[] = useMemo(
    () => (lookups?.sourceTypes ?? ['BANK_ACCOUNT', 'ACCOUNTING_ROLE']).map(t => ({
      value: t,
      label: t === 'BANK_ACCOUNT' ? 'Bank Account' : t === 'ACCOUNTING_ROLE' ? 'Accounting Role' : t,
    })),
    [lookups]
  );

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.sourceType) errors.sourceType = 'Source Type is required';
    if (!formData.sourceKey) errors.sourceKey = formData.sourceType === 'BANK_ACCOUNT'
      ? 'Select the bank account to map'
      : 'Select the accounting role to map';
    if (!formData.ledgerRef) errors.ledgerRef = 'Select the Fairbooks ledger to post to';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const ledger = fbLedgerCacheRef.current[formData.ledgerRef];
      const payload: UpsertAccountingLedgerMapRequest = {
        propertyId: formData.propertyId || undefined,
        sourceType: formData.sourceType,
        sourceKey: formData.sourceKey,
        providerCode: 'FAIRBOOKS',
        ledgerRef: formData.ledgerRef,
        ledgerCode: ledger?.ledgerCode || undefined,
        ledgerName: ledger?.ledgerName || undefined,
        notes: formData.notes.trim() || undefined,
      };

      if (mode === 'edit' && record) {
        await ledgerMappingService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await ledgerMappingService.create(payload);
      }

      setIsSaving(false);
      notificationService.toast.success(`Ledger mapping ${mode === 'edit' ? 'updated' : 'created'} successfully.`);
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message;
      if (status === 409 && mode === 'edit' && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, record, onSave]);

  if (!isOpen) return null;

  const sourceLocked = mode === 'edit';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{mode === 'edit' ? 'Saving...' : 'Creating...'}</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Ledger Mapping' : 'Edit Ledger Mapping'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {sourceLocked && (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              The mapping source is fixed after creation. To map a different bank account or role, delete this row and add a new one.
            </p>
          )}

          <div className="form-grid-2">
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label="Property Scope"
                isAsync
                loadOptions={loadPropertyOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.propertyId}
                onChange={(val) => setFormData(prev => ({ ...prev, propertyId: (val as string) || '' }))}
                placeholder="System default (all properties)"
                isClearable
                isDisabled={sourceLocked}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="form-group" data-field="sourceType">
              <SyutSelect
                label="Source Type"
                options={sourceTypeOptions}
                value={formData.sourceType}
                onChange={(val) => {
                  clearFieldError('sourceType');
                  clearFieldError('sourceKey');
                  setFormData(prev => ({ ...prev, sourceType: (val as string) || 'BANK_ACCOUNT', sourceKey: '' }));
                }}
                isClearable={false}
                isDisabled={sourceLocked}
                size="md"
                error={fieldErrors.sourceType}
              />
            </div>
          </div>

          <div className="form-grid-1" style={{ marginTop: '0.5rem' }}>
            <div className="form-group" data-field="sourceKey">
              {formData.sourceType === 'BANK_ACCOUNT' ? (
                sourceLocked ? (
                  <>
                    <label className="form-label">Bank Account</label>
                    <input type="text" className="form-input" value={record?.sourceDisplay || formData.sourceKey} disabled />
                  </>
                ) : (
                  <SyutSelect
                    label="Bank Account"
                    isAsync
                    loadOptions={loadBankAccountOptions}
                    defaultOptions={true}
                    cacheOptions={false}
                    value={formData.sourceKey}
                    onChange={(val) => { clearFieldError('sourceKey'); setFormData(prev => ({ ...prev, sourceKey: (val as string) || '' })); }}
                    placeholder="Select the bank account..."
                    isClearable
                    size="md"
                    error={fieldErrors.sourceKey}
                    menuPortalTarget={document.body}
                  />
                )
              ) : (
                <SyutSelect
                  label="Accounting Role"
                  options={roleOptions}
                  value={formData.sourceKey}
                  onChange={(val) => { clearFieldError('sourceKey'); setFormData(prev => ({ ...prev, sourceKey: (val as string) || '' })); }}
                  placeholder="Select the role..."
                  isClearable={false}
                  isDisabled={sourceLocked}
                  size="md"
                  error={fieldErrors.sourceKey}
                  menuPortalTarget={document.body}
                />
              )}
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Posts To (Fairbooks)</h3>
            <div className="form-grid-1">
              <div className="form-group" data-field="ledgerRef">
                <SyutSelect
                  label="Fairbooks Ledger"
                  isAsync
                  loadOptions={loadFbLedgerOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={formData.ledgerRef}
                  onChange={(val) => { clearFieldError('ledgerRef'); setFormData(prev => ({ ...prev, ledgerRef: (val as string) || '' })); }}
                  placeholder="Search the chart of accounts..."
                  isClearable
                  size="md"
                  error={fieldErrors.ledgerRef}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <div className="form-group" data-field="notes">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Mapping'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} this ledger mapping? New postings resolve against it immediately.`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="OK"
        cancelLabel="Close"
        variant="default"
        onConfirm={() => { setConcurrencyConflict({ open: false, message: '' }); onSave(); }}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default LedgerMappingModal;
